import { useState, useEffect, useRef } from "react";
import { isPublicRoute as isPublicRouteLib } from './lib/routesPubliques'; // 🚨 routes publiques clients
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Sidebar, SidebarToggle } from "@/components/Sidebar";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import { useTheme } from "@/lib/useTheme";
import { useAppVersionWatcher } from "@/hooks/useAppVersionWatcher";
import { useKioskMode } from "@/hooks/useKioskMode";
import { Users, Camera, Upload, X, Clock, CheckCircle, Briefcase, MapPin } from "lucide-react";
import LiveMapView from "@/pages/LiveMapView";
import FacturePublique from "@/pages/FacturePublique";
import LMDSFacturePublique from "@/pages/LMDSFacturePublique";
import LoginPage from "@/pages/LoginPage";
import { Dashboard } from "@/pages/Dashboard";
import { AnalyseRevenus } from "@/pages/AnalyseRevenus";
import { Rentabilite } from "@/pages/Rentabilite";
import { Production } from "@/pages/Production";
import { Structure } from "@/pages/Structure";
import { Marketing } from "@/pages/Marketing";
import { CashFlow } from "@/pages/CashFlow";
import { ArgentCash } from "@/pages/ArgentCash";
import { Transactions } from "@/pages/Transactions";
import { Comparaison } from "@/pages/Comparaison";
import { AnalyseMarges } from "@/pages/AnalyseMarges";
import { ConfigurationCibles } from "@/pages/ConfigurationCibles";
import { ConfigurationLegal } from "@/pages/ConfigurationLegal";
import { Placements } from "@/pages/Placements";
import { CroissanceIA } from "@/pages/CroissanceIA";
import { ConfigurationReglesCalcul } from "@/pages/ConfigurationReglesCalcul";
import ConfigurationCCQ from "@/pages/ConfigurationCCQ";
import { ConfigurationSoldes } from "@/pages/ConfigurationSoldes";
import ConfigurationMobile from "@/pages/ConfigurationMobile";
import { GestionVentilation } from "@/pages/GestionVentilation";
import { GestionTaxes } from "@/pages/GestionTaxes";
import { ConfigurationAvertissements } from "@/pages/ConfigurationAvertissements";
import { PolitiquesVetements } from "@/pages/PolitiquesVetements";
import { ActifsEtDettes } from "@/pages/ActifsEtDettes";
import { Depenses } from "@/pages/Depenses";
import { VentilationPaie } from "@/pages/VentilationPaie";
import { ClassificationManuelle } from "@/pages/ClassificationManuelle";
import { GestionCash } from "@/pages/GestionCash";
import { Entrepot } from "@/pages/Entrepot";
import KioskEntrepot from "@/pages/KioskEntrepot";
// Performance et MesPerformances fusionnées dans MonBonus
import ValidationHeures from "@/pages/ValidationHeures";
import { GestionBudgets } from "@/pages/GestionBudgets";
import { Inspection } from "@/pages/Inspection";
import { Alertes } from "@/pages/Alertes";
import { GestionEmployes } from "@/pages/GestionEmployes";
import { Pointage, RapportPointage, PointageManager } from "@/pages/Pointage";
// 🗑️ [Phase 33] BTInternes supprimé — la migration manuelle vers BT sous client interne PRQC
// est terminée. La collection `bt_internes` reste en DB par sécurité mais la page n'est plus accessible.

import { ImportHistorique } from "@/pages/ImportHistorique";
import { MappingComptesQB } from "@/pages/MappingComptesQB";
import { MappingCategoriesQB } from "@/pages/MappingCategoriesQB";
import { Terms } from "@/pages/Terms";
import { Privacy } from "@/pages/Privacy";
import { DossiersCRM } from "@/pages/DossiersCRM";
import { CreerDossier } from "@/pages/CreerDossier";
import { DetailsProjet } from "@/pages/DetailsProjet";
import { CreerClient } from "@/pages/CreerClient";
import { ProjetsEnvergure } from "@/pages/ProjetsEnvergure";
import { ListeClients } from "@/pages/ListeClients";
import { FicheClient } from "@/pages/FicheClient";
import { CreerProjet } from "@/pages/CreerProjet";
import { CalendrierRendezVous } from "@/pages/CalendrierRendezVous";
import { CalendrierProduction } from "@/pages/CalendrierProduction";
import { CalendrierEquipements } from "@/pages/CalendrierEquipements";
import { RoutesLavage } from "@/pages/RoutesLavage";
import { CalendrierConges } from "@/pages/CalendrierConges";
import { InformationsEntreprise } from "@/pages/InformationsEntreprise";

import CentreNotifications from "@/pages/CentreNotifications";
import CentreControleIA from "@/pages/CentreControleIA";  // ✅ PHASE 5
import { MessagesPreenregistres } from "@/pages/MessagesPreenregistres";
import { GestionProduits } from "@/pages/GestionProduits";
import { Revente } from "@/pages/Revente";
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples
import SoumissionPublique from "@/pages/SoumissionPublique";
import DechargePublique from "@/pages/DechargePublique";
import AjoutPublique from "@/pages/AjoutPublique";
import PresentationClient from "@/pages/PresentationClient";
import { GestionAvertissements } from "@/pages/GestionAvertissements";
import { GestionBonus } from "@/pages/GestionBonus";
import { MonBonus } from "@/pages/MonBonus";
import { GestionContrats } from "@/pages/GestionContrats";
import { MonEspace } from "@/pages/MonEspace";
import CalendrierProductionEmploye from "@/pages/employe/CalendrierProductionEmploye";
import CalendrierInstallateur from "@/pages/employe/CalendrierInstallateur";
import CalendrierAdmin from "@/pages/employe/CalendrierAdmin";
import { GestionConges } from "@/pages/GestionConges";
import { GestionUtilisateurs } from "@/pages/GestionUtilisateurs";
import { Securite } from "@/pages/Securite";
import RelancesSoumissions from "@/pages/RelancesSoumissions"; // 🔁 Suivi client
import { LMDSSousTraitants } from "@/pages/LMDSSousTraitants";
import { LMDSInstallateur } from "@/pages/LMDSInstallateur";
import { LMDSFacturationGlobale } from "@/pages/LMDSFacturationGlobale";
import { LMDSNonPayees } from "@/pages/LMDSNonPayees";
import GestionInstallateurs from "@/pages/GestionInstallateurs";  // 🧾 Installateurs Phase 1
import MotDePasseOublie from "@/pages/MotDePasseOublie";  // 🔐 Connexion P1
import ReinitialiserMotDePasse from "@/pages/ReinitialiserMotDePasse";  // 🔐 Connexion P1
import AccepterInvitation from "@/pages/AccepterInvitation";  // 🔐 Connexion P3
import { ConnexionQuickBooks } from "@/pages/ConnexionQuickBooks";
import { HistoriqueSMS } from "@/pages/HistoriqueSMS";
import { GestionAcces } from "@/pages/GestionAcces";
import { EmailConfiguration } from "@/pages/EmailConfiguration";
import TodoList from "@/pages/TodoList";
import { Messagerie } from "@/pages/Messagerie";
import { Propositions } from "@/pages/Propositions";
import { Vehicules } from "@/pages/Vehicules";
import Parking from "@/pages/Parking";
import ParkingAdmin from "@/pages/ParkingAdmin";
import ComparaisonMateriel from "@/pages/ComparaisonMateriel";
import SyncFailuresPage from "@/pages/SyncFailuresPage";  // 🆕 2026-05-07 Monitoring sync échouées
import AdminDiagnostics from "@/pages/AdminDiagnostics";  // 🆕 [2A.4] Télescope diagnostics clients
import SmsDestinatairesAdmin from "@/pages/admin/SmsDestinataires";  // 🚀 [Phase v13.55 — 2026-07-22] Config destinataires SMS auto
import HistoriqueInspections from "@/pages/HistoriqueInspections";
import ServicesPage from "@/pages/ServicesPage";
import { AIChat } from "@/components/AIChat";
import { AIHealthMonitor } from "@/components/AIHealthMonitor";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import AutoPunchOutMonitor from "@/components/AutoPunchOutMonitor";
import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageGuard } from "@/components/PageGuard";
import { PermissionsProvider } from "@/lib/PermissionsContext";
import { NotificationsProvider } from "@/lib/NotificationsContext";

// Redirige l'utilisateur vers sa première page autorisée
const HOME_PAGES_ORDERED = [
  { pageKey: "alertes",           path: "/alertes" },
  { pageKey: "projets",           path: "/crm/dossiers" },
  { pageKey: "projets_envergure", path: "/projets-envergure" },
  { pageKey: "clients",           path: "/crm/clients" },
  { pageKey: "rendez_vous",       path: "/crm/calendrier-rendez-vous" },
  { pageKey: "production",        path: "/calendrier-production" },
  { pageKey: "pointage",          path: "/pointage" },
  { pageKey: "validation_heures", path: "/validation-heures" },
  { pageKey: "mon_espace",        path: "/mon-espace" },
  { pageKey: "mon_bonus",         path: "/mon-bonus" },
  { pageKey: "messagerie",        path: "/messagerie" },
  { pageKey: "todo_list",         path: "/todo-list" },
  { pageKey: "employes",          path: "/employes" },
  { pageKey: "lmds",              path: "/sous-traitants/lmds" },
];

function HomeRedirect() {
  const { canSeePage, loading } = usePermissions() || {};
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || loading) return;
    // Les employés terrain atterrissent directement sur Mon Espace
    if (['chef_equipe', 'peintre', 'installateur'].includes(user.role)) {
      navigate('/mon-espace', { replace: true });
      return;
    }
    if (estProprietaire(user)) return; // Propriétaire voit le dashboard
    if (canSeePage && canSeePage("vue_ensemble")) return; // A accès → pas de redirection
    // Chercher la première page autorisée
    const first = HOME_PAGES_ORDERED.find(p => canSeePage && canSeePage(p.pageKey));
    if (first) navigate(first.path, { replace: true });
  }, [loading, user, canSeePage, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;
  if (!user) return null;
  if (estProprietaire(user) || (canSeePage && canSeePage("vue_ensemble"))) return <Dashboard />;
  // Pour les rôles sans Dashboard (installateur, peintre, etc.) — afficher un loading pendant la redirection
  return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;
}

// Remet la page en haut à chaque changement de route
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Layout avec Sidebar pour les pages internes
function AppLayout({ children, sidebarOpen, setSidebarOpen }) {
  const { user, impersonationInfo } = useAuth();
  const { canDo } = usePermissions() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const { isKiosk } = useKioskMode();
  const [showLiveMap, setShowLiveMap] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via matrice `voir_tous_punches`.
  // Fail-secure (canDo=false si permissions non chargées). Backend `ADMIN_PUNCH_ROLES`
  // (lib/permissions_guard.py) est aligné (Vague 2b).
  const canSeeEmployesPunches = !!(canDo && canDo('voir_tous_punches'));
  
  // Calculer si le banner impersonation est visible
  const isImpersonating = impersonationInfo && impersonationInfo.is_impersonation;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 🏭 2026-05-18 — Garde-fou Kiosque : si l'utilisateur sort de /entrepot
  // en mode kiosque (ex : lien interne, navigation back/forward), on le renvoie
  // automatiquement vers /entrepot?kiosk=1. Aucune autre page ne doit être
  // accessible depuis le terminal partagé.
  useEffect(() => {
    if (!isKiosk) return;
    if (!location.pathname.startsWith('/entrepot')) {
      navigate('/entrepot?kiosk=1', { replace: true });
    }
  }, [isKiosk, location.pathname, navigate]);
  return (
    <>
      {!isKiosk && <ImpersonationBanner />}
      {!isKiosk && <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />}
      {!isKiosk && (
        <div className="hidden md:block">
          <SidebarToggle isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        </div>
      )}
      {/* Overlay sombre sur mobile quand la sidebar est ouverte */}
      {!isKiosk && isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Bouton global "Employés punchés" — caché pour chef_equipe, peintre, installateur et en mode kiosque */}
      {!isKiosk && canSeeEmployesPunches && (
      <button
        onClick={() => setShowLiveMap(true)}
        data-testid="btn-live-map"
        className="fixed z-40 flex items-center gap-2 bg-slate-900/80 backdrop-blur hover:bg-blue-700 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full shadow-lg transition-all duration-200 border border-white/10"
        title="Voir les employés punchés en temps réel"
        style={{ 
          top: isImpersonating ? '52px' : '12px', 
          right: '16px',
          pointerEvents: 'auto' 
        }}
      >
        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
        <Users className="w-3.5 h-3.5" />
      </button>
      )}

      <main 
        className={`transition-all duration-300 min-h-screen ${!isKiosk && sidebarOpen && !isMobile ? 'ml-64' : 'ml-0'}`}
        style={{ paddingTop: !isKiosk && isImpersonating ? '45px' : '0' }}
      >
        <ScrollToTop />
        <div className={isKiosk ? "p-4 sm:p-6" : "pt-14 px-3 pb-20 sm:pt-6 sm:pl-6 sm:pr-6 sm:pb-6"}>
          {children}
        </div>
      </main>

      {!isKiosk && showLiveMap && <LiveMapView onClose={() => setShowLiveMap(false)} />}
      {!isKiosk && <AIChat />}
      {!isKiosk && <AIHealthMonitor />}
      {!isKiosk && <MobileBottomNav onToggleSidebar={() => setSidebarOpen(o => !o)} />}
    </>
  );
}

/** ---- Modal Punch-in Bureau (propriétaires) ---- */
function PunchAlertModal({ user, onClose }) {
  const [punching, setPunching] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const handlePunch = async () => {
    if (!user?.employee_id) {
      setError("Aucun compte employé lié à votre utilisateur. Demandez à l'admin de créer/lier votre fiche employé.");
      return;
    }
    setPunching(true);
    setError(null);
    try {
      // GPS obligatoire pour le punch admin aussi
      const { requestGPS } = await import('./lib/gpsUtils');
      const gps = await requestGPS({ required: true });
      if (!gps) {
        setError("Permission GPS refusée — impossible de puncher sans géolocalisation. Activez la permission dans votre navigateur puis réessayez.");
        setPunching(false);
        return;
      }

      const res = await fetch(`${window.__API_URL__ || ""}/api/punch/in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: JSON.stringify({
          employee_id: user.employee_id,
          contrat_id: 'admin_hours',
          type_punch: 'admin',
          employee_nom: `${user.prenom || ''} ${user.nom || ''}`.trim(),
          latitude: gps.latitude,
          longitude: gps.longitude,
        })
      });

      // 🐛 2026-05-15 — Avant : `await fetch(...)` puis setDone(true) sans
      // vérifier le status HTTP, ET catch silencieux. Conséquence : les
      // propriétaires voyaient "Punch enregistré !" même quand l'API renvoyait
      // 400 ("Un punch est déjà en cours" — typique d'un dépunch oublié la
      // veille) ou 500. Aucun feedback → bug fantôme.
      if (!res.ok) {
        let detail = `Erreur HTTP ${res.status}`;
        try {
          const j = await res.json();
          detail = j.detail || j.message || detail;
        } catch { /* response not JSON */ }
        setError(detail);
        setPunching(false);
        return;
      }

      setDone(true);
      setTimeout(onClose, 1800);
    } catch (e) {
      setError(`Erreur réseau : ${e?.message || 'impossible de joindre le serveur'}`);
      setPunching(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
        {done ? (
          <>
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-bold text-green-700">Punch bureau enregistré !</p>
            <p className="text-sm text-slate-500 mt-1">Bonne journée de travail !</p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-7 h-7 text-blue-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-800 mb-2">Bonjour !</h2>
            <p className="text-sm text-slate-600 mb-5">
              Vous faites du <strong>travail de bureau</strong> ce matin ?<br />
              Voulez-vous vous puncher pour vos heures admin ?
            </p>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-left" data-testid="punch-alert-error">
                <p className="text-xs font-semibold text-red-700 mb-1">❌ Impossible de puncher :</p>
                <p className="text-xs text-red-700">{error}</p>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 font-medium text-sm transition-colors">
                {error ? 'Fermer' : 'Non merci'}
              </button>
              <button onClick={handlePunch} disabled={punching}
                className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="btn-punch-bureau">
                {punching ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Clock className="w-4 h-4" />}
                {error ? 'Réessayer' : 'Oui, me puncher'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
        <PermissionsProvider>
          <NotificationsProvider>
          <AppWithAuth />
          </NotificationsProvider>
        </PermissionsProvider>
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

function AppWithAuth() {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const { user, loading, updateToken, refreshUser, impersonationInfo } = useAuth();
  // Appliquer le thème (dark mode) dès le démarrage
  useTheme();
  // 🔄 2026-05-01 — Auto-reload silencieux quand le backend redéploie une nouvelle version.
  // Les utilisateurs (PWA mobile, Safari iOS, desktop) reçoivent automatiquement les corrections
  // sans devoir vider leur cache manuellement. Punchs et tokens préservés.
  useAppVersionWatcher();
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const photoInputRef = useRef(null);
  const photoCameraRef = useRef(null);
  const [showPunchAlert, setShowPunchAlert] = useState(false);
  const [onboardingActif, setOnboardingActif] = useState(true);

  // Charger l'état du onboarding (recharger après chaque login)
  useEffect(() => {
    if (!user) return;
    // 🧾 P5b : la route est gardée (`require_authenticated`, v13.61) — envoyer le jeton sinon 401 systématique dans la console
    const jeton = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    fetch(`${window.__API_URL__ || ""}/api/security/config`, { headers: jeton ? { Authorization: `Bearer ${jeton}` } : {} })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setOnboardingActif(d.onboarding_actif !== false); })
      .catch(() => {});
  }, [user?.id]);

  // Vérifier si l'alerte punch-in doit s'afficher (propriétaire, 7h-9h, 1ère connexion du jour)
  useEffect(() => {
    if (!user || !estProprietaire(user)) return;
    const now = new Date();
    const hour = now.getHours();
    if (hour < 7 || hour >= 9) return; // seulement entre 7h et 9h
    const today = now.toISOString().split('T')[0];
    const lastAlertDay = localStorage.getItem('last_punch_alert_day');
    if (lastAlertDay === today) return; // déjà montré aujourd'hui
    localStorage.setItem('last_punch_alert_day', today);
    const timer = setTimeout(() => setShowPunchAlert(true), 800);
    return () => clearTimeout(timer);
  }, [user]);

  const handleChangePwd = async (e) => {
    e.preventDefault();
    setPwdError("");
    if (newPwd.length < 6) { setPwdError("Min. 6 caractères"); return; }
    if (newPwd !== confirmPwd) { setPwdError("Les mots de passe ne correspondent pas"); return; }
    setPwdSaving(true);
    try {
      const res = await fetch(`${window.__API_URL__ || ""}/api/auth/change-my-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("auth_token")}` },
        body: JSON.stringify({ nouveau_mot_de_passe: newPwd, confirmer: confirmPwd })
      });
      const data = await res.json();
      if (res.ok) { updateToken(data.token); setNewPwd(""); setConfirmPwd(""); }
      else setPwdError(data.detail || "Erreur");
    } catch { setPwdError("Erreur réseau"); }
    finally { setPwdSaving(false); }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setPhotoError("");
  };

  const handlePhotoUpload = async () => {
    if (!photoFile || !user?.employee_id) return;
    setPhotoUploading(true);
    setPhotoError("");
    try {
      const formData = new FormData();
      formData.append("file", photoFile);
      const res = await fetch(
        `${window.__API_URL__ || ""}/api/employees/${user.employee_id}/upload-photo-profil`,
        { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` }, body: formData }
      );
      const data = await res.json();
      if (res.ok && data.photo_url) {
        await refreshUser();
      } else {
        setPhotoError(data.detail || "Erreur lors du téléversement");
      }
    } catch { setPhotoError("Erreur réseau"); }
    finally { setPhotoUploading(false); }
  };

  const isPublicRoute = isPublicRouteLib(window.location.pathname); // 🚨 liste unique : lib/routesPubliques.js

  // 🏭 2026-05-18 — Mode Kiosque Entrepôt : session courte sur terminal partagé.
  // On bypass les overlays "changer mot de passe", "photo obligatoire" et
  // "accepter géolocalisation" car l'ordinateur d'entrepôt est mutualisé entre
  // tous les employés et chacun a déjà fait son onboarding sur son téléphone.
  const isKioskSession =
    new URLSearchParams(window.location.search).get('kiosk') === '1' ||
    (typeof window !== 'undefined' && localStorage.getItem('kiosk_mode') === 'entrepot');

  if (loading && !isPublicRoute) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;
  }

  // Overlay "changer mot de passe" - TOUJOURS actif (sécurité non-bypassable)
  // SAUF en mode Kiosque Entrepôt.
  // 🆕 [Phase 81 — 2026-06-22] Le bypass `isImpersonating` est RETIRÉ.
  // En mode Spectateur, on doit voir EXACTEMENT ce que l'employé voit, y compris
  // les overlays bloquants. Sinon le Spectateur ment sur l'expérience réelle.
  const isImpersonating = impersonationInfo?.is_impersonation;
  if (user?.must_change_password && !isKioskSession) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span style={{fontSize:28}}>🔐</span>
            </div>
            <h2 className="text-xl font-bold text-slate-900">Bienvenue, {user.prenom} !</h2>
            <p className="text-slate-500 text-sm mt-2">
              Pour des raisons de sécurité, vous devez créer un nouveau mot de passe avant de continuer.
            </p>
          </div>
          <form onSubmit={handleChangePwd} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nouveau mot de passe</label>
              <input
                type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)}
                placeholder="Min. 6 caractères" required minLength={6}
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Confirmer le mot de passe</label>
              <input
                type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
                placeholder="Répétez le mot de passe" required
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {pwdError && <p className="text-red-500 text-sm bg-red-50 p-2 rounded">{pwdError}</p>}
            <button
              type="submit" disabled={pwdSaving}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50"
            >
              {pwdSaving ? "Enregistrement..." : "Créer mon mot de passe"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Overlay "Accepter la géolocalisation" si pas encore accepté (première connexion) ET onboarding actif
  // 🆕 [Phase 81 — 2026-06-22] Bypass `isImpersonating` RETIRÉ : fidélité totale
  // mode Spectateur (l'Owner doit voir l'onboarding bloquant tel que l'employé le voit).
  if (onboardingActif && !isKioskSession && user && user.employee_id && !user.must_change_password && user.geolocation_accepted === false) {
    const handleAcceptGeo = async () => {
      // Demander la permission de géolocalisation au navigateur
      try {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async () => {
              // Succès: sauvegarder côté serveur
              try {
                await fetch(`${window.__API_URL__ || ""}/api/auth/accept-geolocation`, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token")}` },
                });
                await refreshUser();
              } catch (e) { console.error("Error:", e); }
            },
            async () => {
              // Refusé mais on accepte quand même côté app (l'important c'est le consentement)
              try {
                await fetch(`${window.__API_URL__ || ""}/api/auth/accept-geolocation`, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token")}` },
                });
                await refreshUser();
              } catch (e) { console.error("Error:", e); }
            }
          );
        } else {
          // Pas de geolocation API disponible, accepter quand même
          await fetch(`${window.__API_URL__ || ""}/api/auth/accept-geolocation`, {
            method: "POST",
            headers: { Authorization: `Bearer ${localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token")}` },
          });
          await refreshUser();
        }
      } catch (e) { console.error("Error:", e); }
    };

    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="geolocation-overlay">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Géolocalisation requise</h2>
            <p className="text-slate-500 text-sm mt-2">
              Pour le bon fonctionnement de l'application (pointage, suivi des chantiers), 
              vous devez accepter la géolocalisation.
            </p>
          </div>
          <button
            onClick={handleAcceptGeo}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
            data-testid="btn-accept-geolocation"
          >
            <MapPin className="w-4 h-4" /> J'accepte la géolocalisation
          </button>
          <p className="text-xs text-slate-400 text-center mt-3">
            Votre position est utilisée uniquement dans le cadre professionnel.
          </p>
        </div>
      </div>
    );
  }

  // Overlay "Photo de profil obligatoire" dès la première connexion, SI onboarding actif
  // 🆕 [Phase 81 — 2026-06-22] Bypass `isImpersonating` RETIRÉ : fidélité totale
  // mode Spectateur (l'Owner doit voir l'onboarding bloquant tel que l'employé le voit).
  if (onboardingActif && !isKioskSession && user?.photo_required && user?.employee_id && !user?.photo_url) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="photo-overlay">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-blue-200">
              {photoPreview
                ? <img src={photoPreview} alt="preview" className="w-20 h-20 rounded-full object-cover" />
                : <Camera className="w-8 h-8 text-blue-400" />
              }
            </div>
            <h2 className="text-xl font-bold text-slate-900">Photo de profil requise</h2>
            <p className="text-slate-500 text-sm mt-2">
              Vous devez ajouter une photo de profil avant de continuer.
            </p>
          </div>

          <div className="space-y-4">
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              onChange={handlePhotoSelect}
              className="hidden"
              id="first-login-photo-gallery"
            />
            <input
              ref={photoCameraRef}
              type="file"
              accept="image/*"
              capture="user"
              onChange={handlePhotoSelect}
              className="hidden"
              id="first-login-photo-camera"
            />
            <div className="flex gap-3">
              <button
                onClick={() => photoInputRef.current?.click()}
                className="flex-1 flex items-center justify-center gap-2 border-2 border-dashed border-blue-300 hover:border-blue-500 text-blue-600 hover:text-blue-700 rounded-xl py-4 transition-colors"
                data-testid="select-photo-gallery-btn"
              >
                <Upload className="w-5 h-5" />
                Galerie
              </button>
              <button
                onClick={() => photoCameraRef?.current?.click()}
                className="flex-1 flex items-center justify-center gap-2 border-2 border-dashed border-green-300 hover:border-green-500 text-green-600 hover:text-green-700 rounded-xl py-4 transition-colors"
                data-testid="select-photo-camera-btn"
              >
                <Camera className="w-5 h-5" />
                Caméra
              </button>
            </div>

            {photoPreview && (
              <button
                onClick={handlePhotoUpload}
                disabled={photoUploading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="upload-photo-btn"
              >
                {photoUploading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Téléversement...</>
                ) : (
                  <><Camera className="w-4 h-4" /> Enregistrer ma photo</>
                )}
              </button>
            )}

            {photoError && <p className="text-red-500 text-sm bg-red-50 p-2 rounded text-center">{photoError}</p>}

            <p className="text-xs text-slate-400 text-center">
              Formats acceptés : JPG, PNG, WebP · Max recommandé : 5 MB
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {showPunchAlert && <PunchAlertModal user={user} onClose={() => setShowPunchAlert(false)} />}
      <AutoPunchOutMonitor user={user} />
      <Routes>
          {/* Route publique SANS sidebar - pour les clients */}
          <Route path="/soumission/:token" element={<SoumissionPublique />} />
          <Route path="/decharge/:token" element={<DechargePublique />} />
          <Route path="/ajout-public/:token" element={<AjoutPublique />} />
          <Route path="/facture/:facture_id" element={<FacturePublique />} />
          <Route path="/facture-lmds/:facture_id" element={<LMDSFacturePublique />} />
          <Route path="/presentation/:token" element={<PresentationClient />} />
          
          {/* Login */}
          <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
          {/* 🔐 Connexion P1 — pages publiques de réinitialisation du mot de passe (hors garde d'authentification) */}
          <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
          <Route path="/reinitialiser-mot-de-passe" element={<ReinitialiserMotDePasse />} />
          <Route path="/accepter-invitation" element={<AccepterInvitation />} />

          {/* 🏭 2026-05-18 — Mode Kiosque Entrepôt : route publique SANS sidebar.
              Auth indépendante (numero_employe + mot de passe → token kiosque 15 min).
              Conçu pour l'ordinateur partagé de l'entrepôt. */}
          <Route path="/kiosk/entrepot" element={<KioskEntrepot />} />
          
          {/* Toutes les autres routes AVEC sidebar - protégées */}
          <Route path="/*" element={
            // 🆕 [Phase v13.62 BLOC 1.1 — 2026-02-26] Hotfix redirect kiosque.
            //   Quand le token kiosque expire (15 min) OU quand la page Entrepôt
            //   crash puis recharge, `user` devient null. Auparavant on redirigeait
            //   TOUJOURS vers `/login` (formulaire courriel/mdp), même en session
            //   kiosque → utilisatrice bloquée. Fix : si `kiosk_mode === 'entrepot'`
            //   est encore présent dans localStorage, on renvoie l'employé sur
            //   `/kiosk/entrepot` (formulaire N° d'employé) pour préserver le flow
            //   kiosque. RCA : RCA_V13_62_REGRESSIONS_RBAC.md.
            !user ? (
              (typeof window !== 'undefined' && localStorage.getItem('kiosk_mode') === 'entrepot')
                ? <Navigate to="/kiosk/entrepot" replace />
                : <Navigate to="/login" replace />
            ) :
            <AppLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
              <Routes>
                <Route path="/" element={<HomeRedirect />} />
                <Route path="/dashboard" element={<Navigate to="/" replace />} />
                <Route path="/revenus" element={<PageGuard pageKey="revenus"><AnalyseRevenus /></PageGuard>} />
                <Route path="/rentabilite" element={<PageGuard pageKey="rentabilite"><Rentabilite /></PageGuard>} />
                <Route path="/production" element={<PageGuard pageKey="production"><Production /></PageGuard>} />
                <Route path="/structure" element={<PageGuard pageKey="structure"><Structure /></PageGuard>} />
                <Route path="/marketing" element={<PageGuard pageKey="marketing"><Marketing /></PageGuard>} />
                <Route path="/cashflow" element={<PageGuard pageKey="cashflow"><CashFlow /></PageGuard>} />
                <Route path="/cash" element={<PageGuard pageKey="gestion_cash"><GestionCash /></PageGuard>} />
                <Route path="/transactions" element={<PageGuard pageKey="transactions"><Transactions /></PageGuard>} />
                <Route path="/depenses" element={<PageGuard pageKey="depenses"><Depenses /></PageGuard>} />
                <Route path="/comparaison" element={<PageGuard pageKey="comparaison"><Comparaison /></PageGuard>} />
                <Route path="/actifs-dettes" element={<PageGuard pageKey="actifs_dettes"><ActifsEtDettes /></PageGuard>} />
                <Route path="/ventilation-paie" element={<PageGuard pageKey="heures_paies"><VentilationPaie /></PageGuard>} />
                <Route path="/classification-manuelle" element={<PageGuard pageKey="classification"><ClassificationManuelle /></PageGuard>} />
                <Route path="/gestion-cash" element={<PageGuard pageKey="gestion_cash"><GestionCash /></PageGuard>} />
                <Route path="/entrepot" element={<PageGuard pageKey="entrepot"><Entrepot /></PageGuard>} />
                {/* Performance + MesPerformances fusionnées dans /mon-bonus */}
                <Route path="/validation-heures" element={<PageGuard pageKey="validation_heures"><ValidationHeures /></PageGuard>} />
                <Route path="/budgets" element={<PageGuard pageKey="budgets"><GestionBudgets /></PageGuard>} />
                <Route path="/inspection" element={<PageGuard pageKey="inspection"><Inspection /></PageGuard>} />
                <Route path="/employes" element={<PageGuard pageKey="employes"><GestionEmployes /></PageGuard>} />
                <Route path="/pointage" element={<PageGuard pageKey="pointage"><Pointage /></PageGuard>} />
                <Route path="/gestion-pointage" element={<PageGuard pageKey="pointage"><PointageManager /></PageGuard>} />
                <Route path="/rapport-heures" element={<PageGuard pageKey="rapport_heures"><RapportPointage /></PageGuard>} />
                {/* 🗑️ [Phase 33] /bt-internes SUPPRIMÉE — migration manuelle terminée vers BT sous client interne PRQC */}
                {/* 🔄 [Phase 16] /bt-internes retiré — utiliser Tous les projets > Opérations internes */}
                <Route path="/alertes" element={<PageGuard pageKey="alertes"><Alertes /></PageGuard>} />
                <Route path="/import-historique" element={<PageGuard pageKey="import_historique"><ImportHistorique /></PageGuard>} />
                <Route path="/mapping-comptes-qb" element={<PageGuard pageKey="quickbooks"><MappingComptesQB /></PageGuard>} />
                <Route path="/mapping-categories-qb" element={<PageGuard pageKey="quickbooks"><MappingCategoriesQB /></PageGuard>} />
                <Route path="/quickbooks" element={<PageGuard pageKey="quickbooks"><ConnexionQuickBooks /></PageGuard>} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/configuration" element={<PageGuard pageKey="configuration"><ConfigurationCibles /></PageGuard>} />
                <Route path="/configuration-legal" element={<PageGuard pageKey="configuration"><ConfigurationLegal /></PageGuard>} />
                <Route path="/placements" element={<PageGuard pageKey="placements"><Placements /></PageGuard>} />
                <Route path="/croissance-ia" element={<PageGuard pageKey="croissance_ia"><CroissanceIA /></PageGuard>} />
                <Route path="/configuration-calcul" element={<PageGuard pageKey="configuration"><ConfigurationReglesCalcul /></PageGuard>} />
                <Route path="/reglages/ccq" element={<PageGuard pageKey="configuration"><ConfigurationCCQ /></PageGuard>} />
                <Route path="/analyse-marges" element={<PageGuard pageKey="marges"><AnalyseMarges /></PageGuard>} />
                <Route path="/configuration-soldes" element={<PageGuard pageKey="configuration"><ConfigurationSoldes /></PageGuard>} />
                <Route path="/configuration-mobile" element={<PageGuard pageKey="configuration"><ConfigurationMobile /></PageGuard>} />
                <Route path="/gestion-ventilation" element={<PageGuard pageKey="heures_paies"><GestionVentilation /></PageGuard>} />
                <Route path="/gestion-taxes" element={<PageGuard pageKey="configuration"><GestionTaxes /></PageGuard>} />
                <Route path="/configuration-avertissements" element={<PageGuard pageKey="configuration"><ConfigurationAvertissements /></PageGuard>} />
                <Route path="/politiques-vetements" element={<PageGuard pageKey="politiques_vetements"><PolitiquesVetements /></PageGuard>} />
                <Route path="/informations-entreprise" element={<PageGuard pageKey="infos_entreprise"><InformationsEntreprise /></PageGuard>} />
                <Route path="/messages-preenregistres" element={<PageGuard pageKey="messages"><MessagesPreenregistres /></PageGuard>} />
                <Route path="/centre-notifications" element={<PageGuard pageKey="centre_notifications"><CentreNotifications /></PageGuard>} />
                <Route path="/centre-controle-ia" element={<PageGuard pageKey="configuration"><CentreControleIA /></PageGuard>} />  {/* ✅ PHASE 5 */}
                <Route path="/configuration-emails" element={<PageGuard pageKey="configuration"><EmailConfiguration /></PageGuard>} />
                <Route path="/gestion-produits" element={<PageGuard pageKey="produits"><GestionProduits /></PageGuard>} />
                <Route path="/revente" element={<PageGuard pageKey="revente"><Revente /></PageGuard>} />
                
                {/* CRM Routes */}
                <Route path="/crm/clients" element={<PageGuard pageKey="clients"><ListeClients /></PageGuard>} />
                <Route path="/crm/clients/:id" element={<PageGuard pageKey="clients"><FicheClient /></PageGuard>} />
                <Route path="/crm/creer-client" element={<PageGuard pageKey="clients"><CreerClient /></PageGuard>} />
                <Route path="/crm/creer-projet" element={<PageGuard pageKey="projets"><CreerProjet /></PageGuard>} />
                <Route path="/crm/calendrier-rendez-vous" element={<PageGuard pageKey="rendez_vous"><CalendrierRendezVous /></PageGuard>} />
                <Route path="/calendrier-production" element={<PageGuard pageKey="production"><CalendrierProduction /></PageGuard>} />
                <Route path="/calendrier-equipements" element={<PageGuard pageKey="equipements"><CalendrierEquipements /></PageGuard>} />
                <Route path="/routes-lavage" element={<PageGuard pageKey="lavage"><RoutesLavage /></PageGuard>} />
                <Route path="/calendrier-conges" element={<PageGuard pageKey="conges"><CalendrierConges /></PageGuard>} />
                <Route path="/crm/dossiers" element={<PageGuard pageKey="projets"><DossiersCRM /></PageGuard>} />
                <Route path="/crm/relances-soumissions" element={<PageGuard pageKey="relances_soumissions"><RelancesSoumissions /></PageGuard>} />
                <Route path="/projets-envergure" element={<PageGuard pageKey="projets_envergure"><ProjetsEnvergure /></PageGuard>} />
                <Route path="/crm/creer-dossier" element={<PageGuard pageKey="projets"><CreerDossier /></PageGuard>} />
                <Route path="/crm/projets/:id" element={<PageGuard pageKey="projets"><DetailsProjet /></PageGuard>} />
                <Route path="/gestion-avertissements" element={<PageGuard pageKey="avertissements"><GestionAvertissements /></PageGuard>} />
                <Route path="/gestion-bonus" element={<PageGuard pageKey="bonus"><GestionBonus /></PageGuard>} />
                <Route path="/mon-bonus" element={<MonBonus />} />
                <Route path="/gestion-contrats" element={<PageGuard pageKey="gestion_contrats"><GestionContrats /></PageGuard>} />
                <Route path="/mon-espace" element={<MonEspace />} />
                <Route path="/mon-planning" element={
                  // 🔒 Cat H — aiguillage UI 3 vues par rôle (dispatch d'identité, PAS RBAC).
                  // Documenté dans CATALOGUE_ACTIONS_PAR_PAGE_V13_61.md — préservé en Vague 5b.
                  aLeRole(user, 'installateur') 
                    ? <CalendrierInstallateur /> 
                    : ['proprietaire','charge_projet','adjoint_admin','representante'].includes(user?.role)
                    ? <CalendrierAdmin />
                    : <CalendrierProductionEmploye />
                } />
                <Route path="/conges" element={<PageGuard pageKey="conges"><GestionConges /></PageGuard>} />
                <Route path="/gestion-utilisateurs" element={<PageGuard pageKey="gestion_utilisateurs"><GestionUtilisateurs /></PageGuard>} />
                <Route path="/securite" element={<PageGuard pageKey="securite"><Securite /></PageGuard>} />
                <Route path="/admin/sync-failures" element={<PageGuard pageKey="configuration"><SyncFailuresPage /></PageGuard>} />
                <Route path="/admin/diagnostics" element={<PageGuard pageKey="configuration"><AdminDiagnostics /></PageGuard>} />
                <Route path="/admin/sms-destinataires" element={<PageGuard pageKey="configuration"><SmsDestinatairesAdmin /></PageGuard>} />
                <Route path="/gestion-acces" element={<PageGuard pageKey="gestion_acces"><GestionAcces /></PageGuard>} />
                <Route path="/todo-list" element={<PageGuard pageKey="todo_list"><TodoList /></PageGuard>} />
                <Route path="/messagerie" element={<Messagerie />} />
                <Route path="/propositions" element={<PageGuard pageKey="propositions"><Propositions /></PageGuard>} />
                {/* Sous-traitants */}
                <Route path="/sous-traitants/lmds" element={<PageGuard pageKey="lmds"><LMDSSousTraitants /></PageGuard>} />
                <Route path="/sous-traitants/lmds/:id" element={<PageGuard pageKey="lmds"><LMDSInstallateur /></PageGuard>} />
                <Route path="/sous-traitants/lmds-facturation" element={<PageGuard pageKey="lmds_facturation"><LMDSFacturationGlobale /></PageGuard>} />
                <Route path="/sous-traitants/lmds-non-payees" element={<PageGuard pageKey="lmds_non_payees"><LMDSNonPayees /></PageGuard>} />
                <Route path="/sous-traitants/gestion-installateurs" element={<PageGuard pageKey="gestion_installateurs"><GestionInstallateurs /></PageGuard>} />
                <Route path="/sms/historique" element={<PageGuard pageKey="messages"><HistoriqueSMS /></PageGuard>} />
                {/* Stock */}
                <Route path="/vehicules" element={<PageGuard pageKey="vehicules"><Vehicules /></PageGuard>} />
                <Route path="/parking" element={<Parking />} />
                <Route path="/parking/admin" element={<PageGuard pageKey="gestion_utilisateurs"><ParkingAdmin /></PageGuard>} />
                <Route path="/unites/comparaison-materiel" element={<PageGuard pageKey="employes"><ComparaisonMateriel /></PageGuard>} />
                <Route path="/inspections-historique" element={<PageGuard pageKey="employes"><HistoriqueInspections /></PageGuard>} />
                <Route path="/services" element={<PageGuard pageKey="configuration"><ServicesPage /></PageGuard>} />
              </Routes>
            </AppLayout>
          } />
        </Routes>
        <Toaster position="top-right" />
    </div>
  );
}

export default App;
